import { Configuration, ProductCategoriesApi } from '@/api';
import useGetProductCategoryQuery from '@/api/queries/useGetProductCategoryQuery';
import useGetSupplierQuery from '@/api/queries/useGetSupplierQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { ProductCategoryCreate, ProductCategoryUpdate } from '@/models/ProductCategory';
import { isSupplierCreatedByStore } from '@/models/Supplier';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import ProductCategoryForm from '@/modules/product/components/ProductCategoryForm';
import { ProductCategoryListPath } from '@/modules/product/paths';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { FolderIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const ProductCategoryEdit = () => {
  const navigate = useNavigate();

  const { productCategoryId } = useParams();

  const { currentUser } = useAuth();

  const { supplierId, storeId } = currentUser();

  const {
    productCategory,
    isLoading: isProductCategoryLoading,
    error: productCategoryError,
    invalidateQuery,
  } = useGetProductCategoryQuery(productCategoryId);

  const {
    supplier,
    isLoading: isSupplierLoading,
    error: supplierError,
  } = useGetSupplierQuery(productCategory?.supplierId);

  const isLoading = isProductCategoryLoading || isSupplierLoading;
  const error = productCategoryError || supplierError;

  const productCategoriesApi = new ProductCategoriesApi(new Configuration(useAuth));

  const handleSubmit = async (categoryUpdate: ProductCategoryCreate | ProductCategoryUpdate) => {
    if (!productCategory) {
      return;
    }

    const updatedProductCategory: ProductCategoryUpdate = {
      ...categoryUpdate,
      id: productCategory.id,
    };

    try {
      await productCategoriesApi.updateProductCategory(productCategory.id, updatedProductCategory);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Product category "${updatedProductCategory.name}" updated successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery();

      navigate(ProductCategoryListPath);
    } catch (error) {
      console.error('Error while updating product category:', error);
    }
  };

  const handleOnDelete = async () => {
    if (!productCategory?.id) return;

    try {
      await productCategoriesApi.deleteProductCategory(productCategory.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Category "${productCategory.name}" deleted successfully!`}
          visible={t.visible}
        />
      ));

      navigate(ProductCategoryListPath);
    } catch (error) {
      console.error('Failed to delete product category:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!productCategory || !supplier || error) {
    return;
  }

  // Show the supplier select field, when the supplier is created by the store (retailer)
  const showSupplierSelect = isSupplierCreatedByStore(supplier, storeId);
  // Show the supplier text, when the user is not a supplier (retailer)
  const showSupplier = !supplierId && !showSupplierSelect;

  return (
    <>
      <SettingsHeader title="Edit Product Category" icon={<FolderIcon className="w-6 h-6" />}>
        <Confirm
          onConfirm={handleOnDelete}
          title="Delete User"
          message={`Are you sure you want to delete user "${productCategory.name}"? This action cannot be undone.`}
          danger
        >
          {(handleClick, loading) => (
            <Button
              onClick={handleClick}
              icon={<XMarkIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
              loading={loading}
              variant="delete"
            >
              Delete Category
            </Button>
          )}
        </Confirm>
      </SettingsHeader>

      <ProductCategoryForm
        onSubmit={handleSubmit}
        productCategory={productCategory}
        supplier={supplier}
        showSupplier={showSupplier}
        showSupplierSelect={showSupplierSelect}
      />
    </>
  );
};

export default ProductCategoryEdit;
